import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { ID } from '../../_kernel/ID'
import { Name } from '../../_kernel/Name'
import { Money } from '../../_kernel/Money'
import { Currency } from '../../_kernel/Currency'
import { Board } from './Board'
import { BoardCode } from './BoardCode'
import { Rate } from './Rate'
import { Room } from './Room'
import { Price } from '../../_kernel/Price'
import type { GetBookingRulesAvailabilityIdResponseType } from '../infrastructure/HTTPBookingRepository/schemas/v2/availability/:availabilityId/bookingRules'
import { BoardGroup } from './BoardGroup'
import { Pax } from '../../shared/domain/Pax'
import { Allotment } from './Allotment'
import { Market } from '../../shared/domain/Market'
import type { Currencies } from '../../_kernel/Currency'
import { DomainError } from '../../_kernel/DomainError'
import { ErrorsCode } from '../../_kernel/ErrorsCode'
import { ListOfTax } from './ListOfTax'
import { Insurance } from './Insurance'
import { Tax } from './Tax'
import { ListOfFee } from './ListOfFee'

export const AvailabilityValidation = z.object({
  id: z.instanceof(ID, { message: '[Availability] id is required' }),
  board: z.instanceof(Board, { message: '[Availability] board is required' }),
  rate: z.instanceof(Rate, { message: '[Availability] rate is required' }),
  room: z.instanceof(Room, { message: '[Availability] room is required' }),
  price: z.instanceof(Price, { message: '[Availability] price is required' }),
  taxes: z.instanceof(ListOfTax).optional(),
  insurance: z.instanceof(Insurance).optional(),
  fees: z.instanceof(ListOfFee).optional(),
  nights: z.number().optional(),
})

export class Availability extends Model {
  static create({
    id,
    board,
    rate,
    room,
    price,
    taxes,
    insurance,
    fees,
    nights,
  }: z.infer<typeof AvailabilityValidation>) {
    AvailabilityValidation.parse({ id, board, rate, room, price, taxes, insurance, fees, nights })
    return new Availability(id, board, rate, room, price, taxes, insurance, fees, nights)
  }

  static fromJSON(json: ReturnType<Availability['toJSON']>) {
    return new Availability(
      ID.create({ value: json.id }),
      Board.fromJSON(json.board),
      Rate.fromJSON(json.rate),
      Room.fromJSON(json.room),
      Price.fromJSON(json.price),
      ListOfTax.fromJSON({ json: json.taxes }),
      Insurance.fromJSONOrUndefined(json.insurance),
      ListOfFee.fromJSON({ json: json.fees }),
      json.nights
    )
  }

  static fromBookingRulesAPI({
    availabilityId,
    json,
  }: {
    availabilityId: string
    json: GetBookingRulesAvailabilityIdResponseType
  }) {
    // Create Board domain object from flat API fields
    const board = Board.create({
      code: BoardCode.fromLiteral(json.board_code),
      name: Name.create({ value: json.board_name }),
      group: BoardGroup.create({
        code: json.board_code,
        name: json.board_name,
      }),
    })

    // Create Rate domain object from flat API fields
    const rate = Rate.fromLiterals({
      paymentPolicy: json.rate_class,
      cancellationPolicyFromDate: json.cancellation_policies[0]?.from,
      cancellationPolicyAmount: json.cancellation_policies[0]?.amount,
      currency: json.currency as Currencies,
      rateType: undefined, // When we get the rate from BookingRules API, we don't want to fetch again
    })

    // Create Room domain object from flat API fields
    const room = Room.create({
      id: ID.create({ value: availabilityId }),
      name: Name.create({ value: json.room_name }),
      pax: Pax.fromAvailabilityApi({
        adults: json.adults,
        children: json.children_ages,
      }),
      allotment: Allotment.create({ value: json.allotment }),
    })

    // Create Price domain object from flat API fields
    const currency = Currency.create({ value: json.currency as Currencies })
    const market = Market.fromBookingRulesAPI({ market: json.market })

    const taxes = ListOfTax.create({
      list: json.taxes.map(tax =>
        Tax.fromJSON({
          amount: tax.amount,
          currency: tax.currency as Currencies,
          included: tax.included,
          name: tax.name,
          client: undefined,
          local: undefined,
        })
      ),
    })

    const price = Price.create({
      total: Money.create({
        amount: json.selling_rate,
        currency,
      }),
      totalWithoutDiscount: Money.create({
        amount: json.bighead_rate,
        currency,
      }),
      totalWithFees: Money.create({
        amount: json.selling_rate,
        currency,
      }),
      perNight: Money.create({
        amount: json.avg_rate,
        currency,
      }),
      perNightWithFees: Money.create({
        amount: json.avg_rate,
        currency,
      }),
      perNightSubtotal: Money.create({
        amount: json.avg_rate,
        currency,
      }),
      subtotal: Money.create({
        amount: json.selling_rate,
        currency,
      }),
      subtotalWithFees: Money.create({
        amount: json.selling_rate,
        currency,
      }),
      subtotalNightlyWithFees: Money.create({
        amount: json.avg_rate,
        currency,
      }),
      market,
    })

    const insurance = json.insurance
      ? Insurance.fromJSONOrUndefined({
          id: availabilityId,
          market: market.value,
          price: {
            amount: json.insurance.amount,
            currency: json.insurance.currency as Currencies,
          },
          description: json.insurance.description,
          terms: { value: json.insurance.terms },
        })
      : undefined

    return new Availability(
      ID.create({ value: availabilityId }),
      board,
      rate,
      room,
      price,
      taxes,
      insurance,
      ListOfFee.create({ list: [] }), // The booking rules api not return summary
      json.nights
    )
  }

  constructor(
    private readonly _id: ID,
    private readonly _board: Board,
    private readonly _rate: Rate,
    private readonly _room: Room,
    private readonly _price: Price,
    private readonly _taxes?: ListOfTax,
    private readonly _insurance?: Insurance,
    private readonly _fees?: ListOfFee,
    protected readonly _nights?: number
  ) {
    super()
  }

  id() {
    return this._id.value
  }

  roomID() {
    return this._room.roomID()
  }

  room() {
    return this._room
  }

  board() {
    return this._board
  }

  boardName() {
    return this._board.name() ?? ''
  }

  boardCode() {
    return this._board.code() ?? ''
  }

  rate() {
    return this._rate
  }

  taxes() {
    return this._taxes
  }

  insurance() {
    return this._insurance
  }

  fees() {
    return this._fees
  }

  isRecheck() {
    return this._rate.isRecheck()
  }

  paymentPolicy() {
    return this._rate.paymentPolicy()
  }

  hasCancellationPolicy() {
    return this._rate.hasCancellationPolicy()
  }

  cancellationPolicyFromDate() {
    return this._rate.cancellationPolicyFromDate()
  }

  cancellationPolicyAmount() {
    return this._rate.cancellationPolicyAmount()
  }

  price() {
    return this._price
  }

  priceTotalAmount() {
    return this._price.totalAmount()
  }

  currency() {
    return this._price.currency()
  }

  hasNights(): this is Availability & { _nights: number } {
    return this._nights !== undefined
  }

  nights(): number {
    return this._nights ?? 0
  }

  updatePrices(bookingRulesAvailability: Availability) {
    if (!bookingRulesAvailability.hasNights()) {
      throw DomainError.withCodes(
        ErrorsCode.FORBIDDEN_UPDATE_AVAILABILITY_WITHOUT_NIGHTS
      ).withCauses(
        new Error('The param of this method must be the result of GetBookingRuleBookingUseCase')
      )
    }

    const nights = bookingRulesAvailability.nights()
    const fees = this._price.totalWithFees.amount - this._price.total.amount

    const feesPerNight = fees / nights

    const nextPrice = this._price.updateFees({
      totalWithFees: bookingRulesAvailability._price.totalWithFees.amount + fees,
      perNightWithFees: bookingRulesAvailability._price.perNightWithFees.amount + feesPerNight,
    })
    return Availability.create({
      id: this._id,
      board: this.board(),
      rate: bookingRulesAvailability.rate(), // to not keep the rateType to recheck and cause a render loop
      room: this.room(),
      price: nextPrice,
      taxes: this._taxes,
      insurance: this._insurance,
      fees: this._fees,
      nights: this._nights,
    })
  }

  priceDiff(priceBefore: Price) {
    return this._price.diff(priceBefore)
  }

  toJSON() {
    return {
      id: this._id.value,
      board: this._board.toJSON(),
      rate: this._rate.toJSON(),
      room: this._room.toJSON(),
      price: this._price.toJSON(),
      taxes: this._taxes?.toJSON() ?? { list: [] },
      insurance: this._insurance?.toJSON() ?? undefined,
      fees: this._fees?.toJSON() ?? { list: [] },
      nights: this._nights,
    }
  }

  /**
   * This method is temporally until PriceDetails component
   * accept a model or a json to build the model from it
   */
  toRoomDetail() {
    return {
      taxDetails: this._taxes?.map(tax => tax.toJSON()),
      additionalFees:
        this._fees?.map(fee => {
          const feeJSON = fee.toJSON()
          return {
            amount: feeJSON.total.amount,
            currency: feeJSON.total.currency,
            local: feeJSON.local,
          }
        }) ?? [],
      subtotal: this._price.subtotal.amount,
      subtotalCurrency: this._price.currency(),
      total: this._price.total.amount,
      totalCurrency: this._price.currency(),
      totalWithFees: this._price.totalWithFees.amount,
      payNow: this.hasCancellationPolicy()
        ? this.cancellationPolicyAmount()
        : this.priceTotalAmount(),
      payLate: this._price.totalAmount() - (this.cancellationPolicyAmount() ?? 0),
      paymentPolicy: this.paymentPolicy(),
      bighead: this._price.totalAmount(),
      bigheadCurrency: this._price.currency(),
      nights: this.nights(),
      payDate: this.cancellationPolicyFromDate(),
      roomsBooked: this.room().name(),
      roomsNumber: this.room().roomsNumber(),
      sellingRate: this._price.totalWithoutDiscount.amount,
      sellingRateCurrency: this._price.currency(),
      pricePerNightSubtotal: this._price.perNightSubtotal.amount,
      pricePerNightSubtotalCurrency: this._price.currency(),
      taxes: this._price.totalWithFees.amount - this._price.total.amount,
      taxesCurrency: this._price.currency(),
    }
  }
}
